import { useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { getMe } from "./api"
import { useAuthStore } from "./store"
import { isAuthError } from "@/lib/api"

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation()
  const setUser = useAuthStore((s) => s.setUser)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: !isAuthenticated,
    // Network errors retry twice; auth errors (401/403) bail immediately so we redirect.
    retry: (failureCount, err) => {
      if (isAuthError(err)) return false
      return failureCount < 2
    },
    retryDelay: (attempt) => 500 * (attempt + 1),
  })

  useEffect(() => {
    if (data) setUser(data)
  }, [data, setUser])

  if (isLoading && !isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Ładowanie...
      </div>
    )
  }

  // Auto-redirect tylko na potwierdzone 401/403 (axios widzi response.status). NetworkError
  // (np. CORS blokuje response, server down) zostawia user na bieżącej stronie z opcją retry
  // ORAZ "Zaloguj się" -- chwilowy blip nie powinien wylogowywać, ale gdy user wie że sesja
  // wygasła, ma szybkie wyjście (bug "Nie można połączyć się... bez nic dalszego" gdy CORS
  // maskuje 401 jako NetworkError).
  if (isError && !isAuthenticated) {
    if (isAuthError(error)) {
      return <Navigate to="/login" state={{ from: location }} replace />
    }
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 text-muted-foreground">
        <p>Nie można połączyć się z serwerem. Sesja mogła wygasnąć lub jest problem z siecią.</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted"
          >
            Ponów
          </button>
          <button
            type="button"
            onClick={() => { window.location.href = '/login' }}
            className="rounded-md border border-sky-600 bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700"
          >
            Zaloguj się
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
